import type { ShiftProductionFormPayload } from "../../../shared/shift-production-form/contracts";

type Product = ShiftProductionFormPayload["products"][number];
type Row = Product["speedChanges"][number] | Product["downtimes"][number] | Product["productChanges"][number];

export type ApprovalRevisionFieldChange = {
  path: string;
  previousValue: string;
  currentValue: string;
  state: "CHANGED" | "REMOVED";
};

export type ApprovalRevisionAddition = {
  tablePath: string;
  text: string;
};

export type ApprovalRevisionDiff = {
  fields: readonly ApprovalRevisionFieldChange[];
  additions: readonly ApprovalRevisionAddition[];
};

const headerFields = [
  "operatorName", "departmentName", "lineName", "productionDate", "shift",
  "checklistExplanation", "generalExplanation", "lateCompletionReason",
] as const;
const productFields = [
  "machineStartedAt", "firstProductAt", "speedDeviationReason", "postChangeMachineStartedAt",
  "postChangeDelayReason", "postChangeFirstProductAt", "postChangeFirstProductDelayReason",
  "explanation",
] as const;
const quantityFields = [
  "scrapKg", "animalFeedKg", "reelWasteQuantity", "boxWasteCount", "cartonWasteCount",
  "labelWasteCount", "targetCarton", "targetCount", "targetKg", "actualCarton",
  "actualCount", "actualKg", "targetNotDefined", "targetExplanation", "wasteReason",
  "wasteResponsibleDepartment",
] as const;
const rowFields = {
  speedChanges: ["occurredAt", "newSpeed", "unit", "reason", "informedPerson", "notInformed"],
  downtimes: ["startedAt", "endedAt", "carriedToNextShift", "reason", "responsibleDepartment", "maintenanceRequested", "maintenanceTicketNo"],
  productChanges: ["previousProduct", "nextProduct", "reason", "participantCount", "responsibleDepartment", "restartAt", "firstProductAt"],
} as const;
const measurementLabels: Record<Product["measurements"][number]["key"], string> = {
  THEORETICAL_LINE_SPEED: "Teorik makina hat hızı",
  ACTUAL_LINE_SPEED: "Makina hat hızı",
  MACHINE_TEMPERATURE: "Makina ısı derecesi",
  OVEN_TEMPERATURE: "Fırın sıcaklığı",
  MOLD_TEMPERATURE: "Kalıp sıcaklığı",
  TUNNEL_TEMPERATURE: "Tünel sıcaklığı",
  UNIT_WEIGHT: "Ürün ağırlığı",
  PRODUCT_MOISTURE: "Ürün nemi",
  AMBIENT_TEMPERATURE: "Ortam sıcaklığı",
  AMBIENT_HUMIDITY: "Ortam nemi",
};

type RowName = keyof typeof rowFields;

/** Maps a selected historical revision to the current revision without hiding removed rows. */
export function approvalRevisionDiff(
  historical: ShiftProductionFormPayload,
  current: ShiftProductionFormPayload,
): ApprovalRevisionDiff {
  const fields: ApprovalRevisionFieldChange[] = [];
  const additions: ApprovalRevisionAddition[] = [];
  const add = (path: string, before: unknown, after: unknown) => {
    if (!same(before, after)) fields.push({ path, previousValue: display(before), currentValue: display(after), state: "CHANGED" });
  };

  for (const key of headerFields) add(key, historical[key], current[key]);

  for (const item of historical.checklist) {
    const after = current.checklist.find((candidate) => candidate.key === item.key);
    if (!after) {
      fields.push({ path: `checklist.${item.key}`, previousValue: display(item.state), currentValue: "Güncel sürümde yok", state: "REMOVED" });
      continue;
    }
    add(`checklist.${item.key}.state`, item.state, after.state);
    add(`checklist.${item.key}.explanation`, item.explanation, after.explanation);
  }
  for (const item of current.checklist) {
    if (!historical.checklist.some((candidate) => candidate.key === item.key)) {
      additions.push({ tablePath: "checklist", text: `${checklistLabel(item.key)} kontrolü eklendi` });
    }
  }

  historical.products.forEach((product, productIndex) => {
    const after = matchingItem(current.products, product, productIndex);
    const prefix = `products.${productIndex}`;
    if (!after) {
      fields.push({ path: prefix, previousValue: `${productIndex + 1}. üretim bölümü`, currentValue: "Güncel sürümde yok", state: "REMOVED" });
      return;
    }
    for (const key of productFields) add(`${prefix}.${key}`, product[key], after[key]);
    add(`${prefix}.delayReason`, product.delayReason ?? product.machineStartNotOccurredReason, after.delayReason ?? after.machineStartNotOccurredReason);
    add(`${prefix}.firstProductNotOccurredReason`, product.firstProductNotOccurredReason, after.firstProductNotOccurredReason);
    for (const key of quantityFields) add(`${prefix}.quantities.${key}`, product.quantities[key], after.quantities[key]);
    for (const measurement of product.measurements) {
      const currentMeasurement = after.measurements.find((candidate) => candidate.key === measurement.key);
      if (!currentMeasurement) {
        fields.push({ path: `${prefix}.measurements.${measurement.key}`, previousValue: displayMeasurement(measurement), currentValue: "Güncel sürümde yok", state: "REMOVED" });
      } else if (!same(measurement.value, currentMeasurement.value) || !same(measurement.unit, currentMeasurement.unit)) {
        fields.push({ path: `${prefix}.measurements.${measurement.key}`, previousValue: displayMeasurement(measurement), currentValue: displayMeasurement(currentMeasurement), state: "CHANGED" });
      }
    }
    for (const measurement of after.measurements) {
      if (!product.measurements.some((candidate) => candidate.key === measurement.key)) {
        additions.push({ tablePath: `${prefix}.measurements`, text: `${measurementLabels[measurement.key]} ölçümü eklendi` });
      }
    }
    (Object.keys(rowFields) as RowName[]).forEach((rowName) => {
      compareRows(prefix, rowName, product[rowName], after[rowName], fields, additions);
    });
  });
  current.products.forEach((product, index) => {
    if (!matchingItem(historical.products, product, index)) {
      additions.push({ tablePath: "products", text: `${index + 1}. üretim bölümü eklendi` });
    }
  });
  return { fields, additions };
}

function compareRows(
  prefix: string,
  rowName: RowName,
  before: readonly Row[],
  after: readonly Row[],
  fields: ApprovalRevisionFieldChange[],
  additions: ApprovalRevisionAddition[],
) {
  before.forEach((row, index) => {
    const current = matchingItem(after, row, index);
    const rowPath = `${prefix}.${rowName}.${index}`;
    if (!current) {
      fields.push({ path: rowPath, previousValue: "Bu sürümde mevcut", currentValue: "Güncel sürümde kaldırıldı", state: "REMOVED" });
      return;
    }
    for (const field of rowFields[rowName]) {
      const beforeValue = Reflect.get(row, field);
      const currentValue = Reflect.get(current, field);
      if (!same(beforeValue, currentValue)) {
        fields.push({ path: `${rowPath}.${field}`, previousValue: display(beforeValue), currentValue: display(currentValue), state: "CHANGED" });
      }
    }
  });
  after.forEach((row, index) => {
    if (!matchingItem(before, row, index)) {
      additions.push({ tablePath: `${prefix}.${rowName}`, text: addedRowText(rowName, row) });
    }
  });
}

function matchingItem<T extends { id?: string | null }>(items: readonly T[], item: T, index: number) {
  return item.id ? items.find((candidate) => candidate.id === item.id) : items[index];
}

function same(left: unknown, right: unknown) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function display(value: unknown) {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Evet" : "Hayır";
  if (typeof value === "string") {
    if (value === "SUITABLE") return "Uygun";
    if (value === "UNSUITABLE") return "Değil";
    if (value === "NOT_APPLICABLE") return "Uygulanmaz";
    if (value === "NOT_CHECKED") return "Kontrol edilmedi";
    if (value === "00_08") return "VR1";
    if (value === "08_16") return "VR2";
    if (value === "16_00") return "VR3";
    const instant = /^\d{4}-\d{2}-\d{2}T/u.test(value) ? new Date(value) : null;
    if (instant && Number.isFinite(instant.getTime())) {
      return new Intl.DateTimeFormat("tr-TR", {
        timeZone: "Europe/Istanbul", dateStyle: "short", timeStyle: "short",
      }).format(instant);
    }
    const date = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(value);
    if (date) return `${date[3]}.${date[2]}.${date[1]}`;
  }
  return String(value);
}

function displayMeasurement(value: Product["measurements"][number]) {
  return value.value === null ? "—" : `${value.value}${value.unit ? ` ${value.unit}` : ""}`;
}

function checklistLabel(key: ShiftProductionFormPayload["checklist"][number]["key"]) {
  return {
    TANK_AND_PIPING: "Tank ve boru tesisatı",
    MOVING_PARTS: "Hareketli aksam",
    GUARDS_SENSORS_SWITCHES: "Muhafaza / sensör / switch",
    MOLD: "Kalıp adeti",
    REEL_PACKAGING_LABEL_BOX: "Bobin / ambalaj / etiket / koli",
  }[key];
}

function addedRowText(rowName: RowName, row: Row) {
  if (rowName === "speedChanges") {
    const value = Reflect.get(row, "newSpeed");
    const unit = Reflect.get(row, "unit");
    const reason = display(Reflect.get(row, "reason"));
    return `Güncel sürümde yeni hız satırı: ${display(value)}${unit ? ` ${unit}` : ""} · ${reason}`;
  }
  if (rowName === "downtimes") {
    const start = display(Reflect.get(row, "startedAt"));
    const end = display(Reflect.get(row, "endedAt"));
    return `Güncel sürümde yeni duruş satırı: ${start}–${end} · ${display(Reflect.get(row, "reason"))}`;
  }
  return `Güncel sürümde yeni ürün değişimi satırı: ${display(Reflect.get(row, "reason"))} · ${display(Reflect.get(row, "participantCount"))} kişi`;
}
